import { useContext, useSyncExternalStore } from "react";
import { FeatureFlagContext } from "./FeatureFlagContext.ts";
import { FeatureFlagName, FlagConfiguration } from "./FeatureFlagManager.ts";

/**
 * Hook to access the FeatureFlagManager instance from context.
 * Throws an error if used outside of a FeatureFlagProvider.
 */
export const useFeatureFlagManager = () => {
  const context = useContext(FeatureFlagContext);
  if (!context?.featureFlagManager) {
    throw new Error(
      "useFeatureFlagManager must be used within a FeatureFlagProvider",
    );
  }
  return context.featureFlagManager;
};

/**
 * Hook to get the list of feature flags and their configurations.
 * Filters out hidden flags unless 'enableHiddenFlags' is enabled.
 */
export const useFeatureFlagList = () => {
  const manager = useFeatureFlagManager();
  const enableHiddenFlags = useFeatureFlagValue("enableHiddenFlags");
  const state = useSyncExternalStore(
    manager.subscribe,
    manager.getState,
    manager.getState,
  );
  return Object.entries(state)
    .filter(([, config]) => enableHiddenFlags || !config?.hidden)
    .map(
      ([flag, config]) =>
        ({
          name: config.name,
          description: config.description,
          value: config.value ?? config.defaultValue,
          flag,
        }) as FlagConfiguration,
    );
};

/**
 * Hook to get the current value of a specific feature flag.
 * Subscribes to changes in the flag's value.
 */
export const useFeatureFlagValue = (flag: FeatureFlagName) => {
  const manager = useFeatureFlagManager();
  return useSyncExternalStore(
    manager.subscribe,
    () => manager.getConfig(flag).value,
    () => manager.getConfig(flag).value,
  );
};
